import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Input,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { fetchPostCallData, postPostCallData } from "../../api/postCallProfile";

const PostCallDetailForm = ({ customerId }) => {
  const [parent, setParent] = useState({
    id: null,
    name: "",
    is_active: false,
  });
  const [children, setChildren] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [confirmIndex, setConfirmIndex] = useState(null);
  const [confirmNoteIndex, setConfirmNoteIndex] = useState(null);
  const toast = useToast();
  const cancelRef = useRef();

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const res = await fetchPostCallData(customerId);
        if (res.data) {
          const p = res.data;

          setParent({ id: p.id, name: p.name, is_active: p.is_active });
          setChildren(p.children || []);
          setNotes(p.note || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [customerId]);

  const handleParentChange = (field, value) => {
    setParent((prev) => ({ ...prev, [field]: value }));
  };

  const handleChildChange = (index, field, value) => {
    setChildren((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };

  const handleNoteChange = (index, field, value) => {
    setNotes((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };

  const addChild = () => {
    setChildren((prev) => [
      ...prev,
      { bank_name: "", has_plaid_connection: false, has_csv: false },
    ]);
  };

  const addNote = () => {
    setNotes((prev) => [...prev, { note: "", is_active: true }]);
  };

  const deleteChild = (index) => {
    setConfirmIndex(index);
  };

  const deleteNote = (index) => {
    setConfirmNoteIndex(index);
  };

  const confirmDelete = () => {
    setChildren((prev) => {
      const updated = [...prev];
      if (updated[confirmIndex].id) {
        updated[confirmIndex].is_active = false;
      } else {
        updated.splice(confirmIndex, 1);
      }
      return updated;
    });
    setConfirmIndex(null);
  };

  const confirmNoteDelete = () => {
    setNotes((prev) => {
      const updated = [...prev];
      if (updated[confirmNoteIndex].id) {
        updated[confirmNoteIndex].is_active = false;
      } else {
        updated.splice(confirmNoteIndex, 1);
      }
      return updated;
    });
    setConfirmNoteIndex(null);
  };

  const cancelDelete = () => {
    setConfirmIndex(null);
    setConfirmNoteIndex(null);
  };

  const saveAll = async () => {
    const hasEmptyNoteTitle = notes.some(
      (n) => !n.title || n.title.trim() === ""
    );

    if (hasEmptyNoteTitle) {
      toast({
        title: "Validation Error",
        description: "All note titles must be filled.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!parent.name || parent.name.trim() === "") {
      toast({
        title: "Validation Error",
        description: "Name cannot be empty.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const hasEmptyBankName = children.some(
      (child) => !child.bank_name || child.bank_name.trim() === ""
    );

    if (hasEmptyBankName) {
      toast({
        title: "Validation Error",
        description: "Bank name cannot be empty.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const payload = {
        ...parent,
        children,
        note: notes,
      };
      await postPostCallData(customerId, payload);
      toast({
        title: "Success",
        description: "Parent, children, and notes saved successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save data.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) return <Box>Loading...</Box>;

  return (
    <Box bg="brand.50" p={10}>
      {/* Parent Info */}
      <TableContainer bg={"white"}>
        <Table variant="unstyled">
          <Tbody>
            <Tr>
              <Td
                fontWeight="bold"
                bg="gray.500"
                color={"white"}
                border={"1px solid black"}
              >
                QC Access
              </Td>
              <Td border={"1px solid black"}>
                <Select
                  size="sm"
                  value={parent.is_active ? "yes" : "no"}
                  onChange={(e) =>
                    handleParentChange("is_active", e.target.value === "yes")
                  }
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </Select>
              </Td>
            </Tr>
            <Tr>
              <Td
                bg="gray.500"
                fontWeight="bold"
                color={"white"}
                border={"1px solid black"}
              >
                Name
              </Td>
              <Td border={"1px solid black"}>
                <Input
                  size="sm"
                  value={parent.name}
                  onChange={(e) => handleParentChange("name", e.target.value)}
                />
              </Td>
            </Tr>
          </Tbody>
        </Table>

        {/* Children Table */}
        <Box borderX={"1px solid black"} height={"40px"}></Box>
        <Table variant="unstyled">
          <Thead bg="gray.500">
            <Tr>
              <Th border={"1px solid black"} color={"white"}>
                Bank Name
              </Th>
              <Th border={"1px solid black"} color={"white"}>
                Plaid Connection
              </Th>
              <Th border={"1px solid black"} color={"white"}>
                CSV in Folder
              </Th>
              <Th border={"1px solid black"} color={"white"}>
                Options
              </Th>
            </Tr>
          </Thead>
          <Tbody border={"1px solid black"}>
            {children
              .filter((c) => c.is_active !== false)
              .map((child, index) => (
                <Tr key={index}>
                  <Td border={"1px solid black"}>
                    <Input
                      size="sm"
                      value={child.bank_name}
                      onChange={(e) =>
                        handleChildChange(index, "bank_name", e.target.value)
                      }
                    />
                  </Td>
                  <Td border={"1px solid black"}>
                    <Select
                      size="sm"
                      value={child.has_plaid_connection ? "yes" : "no"}
                      onChange={(e) =>
                        handleChildChange(
                          index,
                          "has_plaid_connection",
                          e.target.value === "yes"
                        )
                      }
                    >
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </Select>
                  </Td>
                  <Td border={"1px solid black"}>
                    <Select
                      size="sm"
                      value={child.has_csv ? "yes" : "no"}
                      onChange={(e) =>
                        handleChildChange(
                          index,
                          "has_csv",
                          e.target.value === "yes"
                        )
                      }
                    >
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </Select>
                  </Td>
                  <Td border={"1px solid black"}>
                    <Button
                      size="xs"
                      colorScheme="red"
                      onClick={() => deleteChild(index)}
                    >
                      Delete
                    </Button>
                  </Td>
                </Tr>
              ))}
          </Tbody>
        </Table>

        {/* Notes Table */}
        <Box borderX={"1px solid black"} height={"40px"}></Box>
        <Table variant="unstyled">
          <Thead bg="gray.500">
            <Tr>
              <Th border={"1px solid black"} color={"white"}>
                Note
              </Th>
              <Th border={"1px solid black"} color={"white"}>
                Options
              </Th>
            </Tr>
          </Thead>
          <Tbody border={"1px solid black"}>
            {notes
              .filter((n) => n.is_active !== false)
              .map((note, index) => (
                <Tr key={index}>
                  <Td border="1px solid black">
                    <VStack spacing={2} align="stretch">
                      <Input
                        size="sm"
                        placeholder="Title"
                        value={note.title || ""}
                        onChange={(e) =>
                          handleNoteChange(index, "title", e.target.value)
                        }
                      />
                      <Textarea
                        size="sm"
                        placeholder="Note"
                        value={note.note}
                        onChange={(e) =>
                          handleNoteChange(index, "note", e.target.value)
                        }
                      />
                    </VStack>
                  </Td>
                  <Td border="1px solid black" width="80px">
                    <Button
                      size="xs"
                      colorScheme="red"
                      onClick={() => deleteNote(index)}
                    >
                      Delete
                    </Button>
                  </Td>
                </Tr>
              ))}
          </Tbody>
        </Table>
      </TableContainer>

      <Box display="flex" gap={2} mt={6}>
        <Button size="sm" colorScheme="green" onClick={addChild}>
          Add Row
        </Button>
        <Button size="sm" colorScheme="purple" onClick={addNote}>
          Add Note
        </Button>
        <Button size="sm" colorScheme="blue" onClick={saveAll}>
          Save All
        </Button>
      </Box>

      <AlertDialog
        isOpen={confirmIndex !== null || confirmNoteIndex !== null}
        leastDestructiveRef={cancelRef}
        onClose={cancelDelete}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete {confirmNoteIndex !== null ? "Note" : "Child"}
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this item? It will be marked as
              inactive and hidden until saved.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={cancelDelete}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={
                  confirmNoteIndex !== null ? confirmNoteDelete : confirmDelete
                }
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default PostCallDetailForm;
